<template>
    <div class="container">
        <!-- header with filters -->
        <div class="pt-16 lg:pt-20 pb-6">
            <div class="flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-8">
                <div class="flex items-center gap-2 font-bold uppercase cursor-pointer lg:cursor-auto" @click="filterOpen = !filterOpen">
                    <icon :name="filterOpen ? 'chevron-up' : 'settings'" class="lg:hidden w-4 h-4 fill-current"/>
                    <span>{{ labels.filter }}</span>
                    <span v-if="selected.length > 0" class="lg:hidden">({{ selected.length }})</span>
                </div>
                <div class="items-center flex-wrap gap-4 lg:gap-2 mb-6 lg:mb-0"
                     :class="{'hidden lg:flex': !filterOpen, 'flex': filterOpen}"
                >
                    <label v-for="category in filterCategories"
                           :key="category.id"
                           class="flex items-center gap-2.5 text-sm bg-white border border-light-gray rounded cursor-pointer py-2 px-4 group"
                    >
                        <icon v-if="isSelected(category.id)" name="close" class="w-2.5 h-2.5 stroke-current"/>
                        <span class="text-gray font-medium group-hover:text-opacity-100"
                              :class="{'text-opacity-100': isSelected(category.id), 'text-opacity-70': !isSelected(category.id)}"
                        >
                            {{ category.label }}
                        </span>
                        <input class="hidden" type="checkbox" :id="category.id" :value="category.id" v-model="selected">
                    </label>
                </div>
            </div>
            <h2 class="text-center text-story-intro font-bold mt-12 lg:mt-20">{{ labels.headline }}</h2>
            <div class="flex items-center gap-4 lg:gap-6 mt-6 lg:mt-10">
                <div class="border-b flex-grow"/>
                <span class="font-bold lg:font-medium">{{ totalItems }} {{ labels.results }}</span>
            </div>
        </div>
        <!-- search results -->
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-y-12 lg:gap-y-10 gap-x-7 mb-6">
            <a v-for="(item, index) in items" :key="index" class="flex flex-col border-b pb-6 lg:pb-4 group" :href="item.link">
                <div class="relative h-0 overflow-hidden pt-4/3 mb-8">
                    <img :src="item.imageUrls.lg || fallbackImage" :alt="item.title" class="absolute top-0 left-0 w-full h-full object-cover transform transition-all ease-out duration-500 group-hover:scale-110"/>
                </div>
                <div v-if="item.title" class="text-2xl lg:text-story-intro font-bold mb-3 lg:mb-4">{{ item.title }}</div>
                <p v-if="item.text" class="text-lg font-light line-clamp-4 max-h-28">{{ item.text }}</p>
                <div class="flex-grow"/>
                <div class="flex flex-wrap gap-2 mt-10">
                    <span v-for="(category, index) in (item.categories || []).slice(0, 5)" :key="index" class="text-sm text-gray text-opacity-70 bg-inactive-gray rounded px-4 py-2">
                        {{ category }}
                    </span>
                </div>
            </a>
        </div>
        <div v-show="loading" class="flex justify-center">
            <loading-spinner class="w-20 h-20 fill-current"/>
        </div>
        <!-- pagination -->
        <div v-if="hasMore" class="flex justify-center text-dark-gray mt-12 text-center">
            <div class="flex items-center gap-3 cursor-pointer" @click="loadMore">
                <icon name="chevron-up" class="w-8 h-8 fill-current transform rotate-180"/>
                <span class="text-2xl font-bold">{{ labels.more }} ({{ more }})</span>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import Icon from '../base/Icon.vue';
import axios from 'axios';
import LoadingSpinner from '../base/LoadingSpinner.vue';
import { addToState, removeFromState } from '../../utils';

export interface Category {
    id: string;
    label: string;
}

export interface SearchLabels {
    filter: string;
    headline: string;
    results: string;
    more: string;
}

export interface Result {
    imageUrls: Record<string, string>;
    title: string;
    text: string;
    link: string;
    categories: string[];
}

export interface ResultResponse {
    totalResults: number;
    teasers: Result[];
}

@Component({
    components: {
        LoadingSpinner,
        Icon
    }
})
export default class SearchResults extends Vue {
    @Prop({ default: () => [] }) roots: string[];
    @Prop({ default: {} }) defaultCategories: Category[];
    @Prop({ default: {} }) filterCategories: Category[];
    @Prop({ default: {} }) labels: SearchLabels;
    @Prop({ default: 6 }) batchSize: number;
    @Prop({ required: true }) fallbackImage: string;

    apiBaseUrl = '/.rest/api/v2/teasers';

    selected: string[] = [];
    filterOpen = false;
    loading = false;

    totalItems = 0;
    offset = 0;
    items: Result[] = [];

    mounted(): void {
        if (!this.readHash()) {
            this.getResults(true);
        }
    }

    get apiUrl(): string {
        return `${this.$contextPath}${this.apiBaseUrl}?site=${this.$site}&locale=${this.$lang}&offset=${this.offset}` +
               `&limit=${this.batchSize}&${this.rootsString}&${this.selectedCategoriesString}`;
    }

    get rootsString(): string {
        return this.roots.map(s => 'root=' + s).join('&');
    }

    get selectedCategoriesString(): string {
        if (this.selected.length > 0) {
            return this.selected.map(s => 'category=' + s).join('&');
        }
        return this.defaultCategories.map(c => c.id).map(s => 'category=' + s).join('&');
    }

    get hasMore(): boolean {
        return this.totalItems > this.items.length;
    }

    get more(): number {
        return Math.min(this.batchSize, this.totalItems - this.items.length);
    }

    loadFromUrl(): void {
        const params = new URLSearchParams(window.location.search);
        if (params.has('categories')) {
            this.selected = params.get('categories').split(',');
        } else {
            this.getResults(true);
        }
    }

    isSelected(id: string): boolean {
        return this.selected.includes(id);
    }

    loadMore(): void {
        this.getResults(false);
    }

    readHash(): boolean {
        const hash = location.hash.replace(/^#/, '');
        const filter = hash.split('&').find(x => x.includes('categories:'));
        if (filter) {
            const split = filter.split(':');
            if (split.length === 2 && split[1].length > 0) {
                this.selected = split[1].split(',');
                return true;
            }
        }
        return false;
    }

    updateHash() {
        removeFromState('categories');
        this.selected.forEach(cat => {
            addToState('categories', cat);
        });
    }

    getResults(clear: boolean): void {
        this.loading = true;
        if (clear) {
            this.offset = 0;
            this.items = [];
        }
        axios.get(this.apiUrl)
            .then(res => {
                const result: ResultResponse = res.data;
                this.totalItems = result.totalResults;
                this.items.push(...result.teasers);
                this.offset = this.items.length;
            })
            .finally(() => {
                this.loading = false;
            });
    }

    @Watch('selected')
    watchSelected(): void {
        this.updateHash();
        this.getResults(true);
    }
}
</script>
